import {DocumentInfoResponseData} from "../../../types/client";
import React, {useEffect, useState} from "react";
import {DocumentIcon} from "@heroicons/react/24/outline";
import styles from "./AdminDocumentInfo.module.css";
import {
    deleteDocumentsAPI,
    getAdminDocumentAPI,
    getAdminDocumentFileAPI,
    getDocumentScopesAPI
} from "../../../services/admin";
import UpdateScopePopUp from "../../../views/admin/popup/UpdateScopePopUp";
import {useNavigate} from "react-router-dom";
import {useAlert} from "../../common/error/AlertContext";
import Button from "../../common/button/Button";

interface AdminDocumentInfoProps {
    documentId: number;
}

//문서 상세 정보
const AdminDocumentInfo: React.FC<AdminDocumentInfoProps> = ({documentId}) => {
    const {showAlert} = useAlert();
    const [data, setData] = useState<DocumentInfoResponseData | null>(null);
    const [loading, setLoading] = useState(true);
    const [fileUrl, setFileUrl] = useState<string | null>(null);
    const [clientScopes, setClientScopes] = useState<string[]>([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const navigate = useNavigate();
    const {showConfirm} = useAlert();

    //데이터 설정
    const fetchData = async () => {
        try {
            setLoading(true);
            const [documentResponse, scopesResponse] = await Promise.all([
                //문서 조회 API
                getAdminDocumentAPI(documentId),
                //문서 공개 대상 조회 API
                getDocumentScopesAPI(documentId)
            ]);
            //데이터 설정
            setData(documentResponse);
            setClientScopes(scopesResponse);
            //문서 첨부파일 조회 API
            const data = await getAdminDocumentFileAPI(documentId);
            //파일 내부 URL 생성
            const url = URL.createObjectURL(data);
            setFileUrl(url);
        } catch (error) {
            console.error('Error fetching document:', error);
        } finally {
            setLoading(false);
        }
    }

    //공개 대상 클라이언트 변경 팝업 열기
    const handleModalOpen = () => {
        setIsModalOpen(true);
    }

    //공개 대상 클라이언트 변경 팝업 닫기
    const handleModalClose = () => {
        setIsModalOpen(false);
        fetchData();
    }

    useEffect(() => {
        fetchData();
    }, [documentId]);

    //날짜 형식 변격, YYYY-MM-DD(요일)
    const formatDate = (date: Date) => {
        const d = new Date(date);
        return `${d.getFullYear().toString()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} (${['일', '월', '화', '수', '목', '금', '토'][d.getDay()]})`;
    }

    //공개 대상 클라이언트 3명 이상인 경우, 3명째부터 ...으로 표시
    const formatClientScopes = (scopes: string[]) => {
        if (scopes.length <= 2) {
            return scopes.join(', ');
        }
        return `${scopes[0]}, ${scopes[1]}...`;
    }

    //문서 수정 페이지로 이동
    const handleEdit = () => {
        navigate(`/admin/documents/${documentId}/edit`);
    }

    //문서 삭제
    const handleDelete = async () => {
        //확인창
        showConfirm(
            '문서를 삭제하시겠습니까?',
            async () => {
                try {
                    const ids = new Set<number>();
                    ids.add(documentId);
                    //문서 삭제 API
                    await deleteDocumentsAPI(ids);
                    showAlert('문서가 삭제되었습니다.', "info");
                    //삭제 후 문서 테이블 페이지로 이동
                    navigate('/admin/documents');
                } catch (error) {
                    console.error('Delete failed:', error);
                }
            }
        )
    }

    if (loading || !data) {
        return <div className={styles.loading}>Loading...</div>;
    }

    return (
        <div className={styles.container}>
            <div className={styles.documentCard}>
                <div className={styles.header}>
                    <div className={styles.headerContent}>
                        {/*문서 제목*/}
                        <h1 className={styles.title}>
                            {data.title}
                            <span className={styles.titleType}>{data.documentType}</span>
                        </h1>
                        <div className={styles.dateContainer}>
                            {/*공개 대상 클라이언트 변경 버튼*/}
                            <button
                                className={styles.clientButton}
                                onClick={handleModalOpen}
                            >
                                공개 대상 클라이언트 : <span
                                className={styles.clientList}>{formatClientScopes(clientScopes)}</span>
                            </button>
                            <span>{formatDate(data.createdAt)}</span>
                        </div>
                    </div>
                    <div className={styles.actionButtons}>
                        <div className={styles.editDeleteButtons}>
                            {/*수정 버튼*/}
                            <Button
                                onClick={handleEdit}
                                variant="primary"
                            >
                                수정
                            </Button>
                            {/*삭제 버튼*/}
                            <Button
                                onClick={handleDelete}
                                variant="secondary"
                            >
                                삭제
                            </Button>
                        </div>
                    </div>
                </div>

                <div className={styles.content}>
                    {/*본문*/}
                    <div className={styles.documentText}>
                        {data.content}
                    </div>
                </div>

                {/*첨부 파일 있는 경우 파일명 출력*/}
                {data.fileName && fileUrl && (
                    <div className={styles.fileSection}>
                        {/*클릭 시 다운로드*/}
                        <a href={fileUrl} download={data.fileName} className={styles.fileLink}>
                            <DocumentIcon className={styles.fileIcon}/>
                            <span className={styles.fileName}>{data.fileName}</span>
                        </a>
                    </div>
                )}
            </div>
            {/*공개 대상 클라이언트 수정 팝업*/}
            <UpdateScopePopUp
                isOpen={isModalOpen}
                documentId={documentId}
                onClose={handleModalClose}/>
        </div>
    );
}

export default AdminDocumentInfo;